import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import styles from "./dataTableV2.module.scss";

const SortIcon = ({ direction }) => (
  <span className={`${styles.sort_icon} ${direction ? styles[`sort_${direction}`] : styles.sort_default}`} aria-hidden="true" />
);

const EmptyState = ({ message }) => (
  <div className={styles.empty_state}>
    <span className={styles.empty_icon}>⊘</span>
    <p>{message}</p>
  </div>
);

const parseWidth = (w) => {
  if (!w) return 150;
  const n = parseInt(w, 10);
  return isNaN(n) ? 150 : n;
};

export const DataTableV2 = ({
  columns = [],
  data = [],
  onRowClick = null,
  onSort = null,
  rowSelect = false,
  onRowSelect = null,
  emptyMessage = "No records to display",
  loading = false,
  ...rest
}) => {
  const { sortBy = { column: null, direction: null } } = rest;
  const [selectedIds, setSelectedIds] = useState(new Set());

  const [colWidths, setColWidths] = useState(() => Object.fromEntries(columns.map((col) => [col.accessor, parseWidth(col.width)])));
  const resizeRef = useRef(null); // { accessor, startX, startWidth }
  const [resizingCol, setResizingCol] = useState(null);

  const fixedBodyRef = useRef(null);
  const scrollBodyRef = useRef(null);
  const scrollHeadRef = useRef(null);

  const fixedColumns = columns.filter((col) => col.fixed);
  const scrollColumns = columns.filter((col) => !col.fixed);
  const hasFixedColumns = fixedColumns.length > 0;

  useEffect(() => {
    setColWidths((prev) => {
      const next = { ...prev };
      columns.forEach((col) => {
        if (!(col.accessor in next)) next[col.accessor] = parseWidth(col.width);
      });
      return next;
    });
  }, [columns]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!resizeRef.current) return;
      const { accessor, startX, startWidth } = resizeRef.current;
      const minWidth = 60;
      const delta = e.clientX - startX;
      const newWidth = Math.max(minWidth, startWidth + delta);
      setColWidths((prev) => ({ ...prev, [accessor]: newWidth }));
    };

    const onMouseUp = () => {
      if (!resizeRef.current) return;
      resizeRef.current = null;
      setResizingCol(null);
      document.body.classList.remove(styles.resizing_cursor);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  const handleResizeStart = useCallback(
    (e, accessor) => {
      e.preventDefault();
      e.stopPropagation();
      resizeRef.current = {
        accessor,
        startX: e.clientX,
        startWidth: colWidths[accessor] ?? 150,
      };
      setResizingCol(accessor);
      document.body.classList.add(styles.resizing_cursor);
    },
    [colWidths]
  );

  // ─── Scroll Sync ──────────────────────────────────────────────────────────
  useEffect(() => {
    const scrollBody = scrollBodyRef.current;
    const fixedBody = fixedBodyRef.current;
    const scrollHead = scrollHeadRef.current;
    if (!scrollBody) return;

    const handleScroll = () => {
      if (fixedBody) fixedBody.scrollTop = scrollBody.scrollTop;
      if (scrollHead) scrollHead.scrollLeft = scrollBody.scrollLeft;
    };

    scrollBody.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollBody.removeEventListener("scroll", handleScroll);
  }, [data]);

  // ─── Sort ───────────────────────────────────────────────────────────────────
  const handleSort = useCallback(
    (accessor) => {
      if (!onSort) return;
      const next =
        sortBy.column !== accessor
          ? { column: accessor, direction: "asc" }
          : sortBy.direction === "asc"
          ? { column: accessor, direction: "desc" }
          : { column: accessor, direction: "asc" };
      onSort(next.column, next.direction);
    },
    [onSort, sortBy]
  );

  // ─── Row Selection ─────────────────────────────────────────────────────────
  const selectableRows = data.filter((row) => !row.isDisabled);

  const handleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelectableIds = new Set(selectableRows.map((_, i) => i));
      const isAllSelected = selectableRows.every((_, i) => prev.has(i));
      const next = isAllSelected ? new Set() : allSelectableIds;
      onRowSelect?.(isAllSelected ? [] : [...selectableRows]);
      return next;
    });
  }, [selectableRows, onRowSelect]);

  const handleSelectRow = useCallback(
    (row, index, e) => {
      e.stopPropagation();
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.has(index) ? next.delete(index) : next.add(index);
        const selectedRows = data.filter((_, i) => next.has(i));
        onRowSelect?.(selectedRows);
        return next;
      });
    },
    [data, onRowSelect]
  );

  const allSelected = selectableRows.length > 0 && selectableRows.every((_, i) => selectedIds.has(i));
  const someSelected = selectableRows.some((_, i) => selectedIds.has(i));

  const getCellValue = (row, col) => {
    const value = row[col.accessor];
  
    if (col.Cell) {
      return col.Cell({ value, row });
    }
  
    if (col.type === "image") {
      return (
        <div className={styles.image_wrapper}>
          <img
            src={value}
            alt={col.Header}
            className={styles.table_image}
            onError={(e) => {
              e.currentTarget.style.display = "none";
              e.currentTarget.nextSibling.style.display = "block";
            }}
            loading="lazy"
          />
  
          <div className={styles.image_placeholder} />
        </div>
      );
    }
  
    return value ?? "—";
  };

  const getColStyle = (col) => {
    const w = `${colWidths[col.accessor] ?? parseWidth(col.width)}px`;
    return { width: w, minWidth: w, maxWidth: w };
  };

  const renderHeaderCell = (col, idx) => (
    <div
      key={col.accessor || idx}
      className={`${styles.th} ${onSort && col.sortable ? styles.th_sortable : ""}`}
      style={getColStyle(col)}
      onClick={() => col.sortable && handleSort(col.accessor)}
      role={col.sortable ? "button" : undefined}
      aria-sort={sortBy.column === col.accessor ? sortBy.direction : undefined}
    >
      <span className={styles.th_label}>{col.Header}</span>
      {onSort && col.sortable && <SortIcon direction={sortBy.column === col.accessor ? sortBy.direction : null} />}
      <div
        className={`${styles.resize_handle} ${resizingCol === col.accessor ? styles.resize_active : ""}`}
        onMouseDown={(e) => handleResizeStart(e, col.accessor)}
        onClick={(e) => e.stopPropagation()}
        title="Drag to resize column"
      />
    </div>
  );

  const renderBodyCell = (row, col, idx) => (
    <div
      key={col.accessor || idx}
      className={styles.td}
      style={getColStyle(col)}
      title={typeof row[col.accessor] === "string" ? row[col.accessor] : undefined}
    >
      <span className={styles.td_content}>{getCellValue(row, col)}</span>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.skeleton_header} />
        {[...Array(5)].map((_, i) => (
          <div key={i} className={styles.skeleton_row} style={{ animationDelay: `${i * 0.07}s` }} />
        ))}
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className={styles.wrapper}>
        <div className={`${styles.thead_row} ${styles.thead_empty}`}>
          {rowSelect && <div className={`${styles.th} ${styles.th_checkbox}`} />}
          {columns.map(renderHeaderCell)}
        </div>
        <EmptyState message={emptyMessage} />
      </div>
    );
  }

  if (hasFixedColumns) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.split_layout}>
          {/* Fixed Side */}
          <div className={styles.fixed_side}>
            <div className={`${styles.thead_row} ${styles.fixed_thead}`}>
              {rowSelect && (
                <div className={`${styles.th} ${styles.th_checkbox}`}>
                  <input
                    type="checkbox"
                    className={styles.checkbox}
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </div>
              )}
              {fixedColumns.map(renderHeaderCell)}
            </div>
            <div className={styles.fixed_body} ref={fixedBodyRef}>
              {data.map((row, rowIdx) => (
                <div
                  key={rowIdx}
                  className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${selectedIds.has(rowIdx) ? styles.tr_selected : ""} ${
                    row.strike_through ? styles.tr_strike : ""
                  }`}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`selectedRow${rowIdx}`}
                >
                  {rowSelect && (
                    <div className={`${styles.td} ${styles.td_checkbox}`}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={selectedIds.has(rowIdx)}
                        disabled={row.isDisabled}
                        onChange={(e) => handleSelectRow(row, rowIdx, e)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select row ${rowIdx + 1}`}
                      />
                    </div>
                  )}
                  {fixedColumns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
                </div>
              ))}
            </div>
          </div>

          <div className={styles.scroll_side}>
            <div className={styles.scroll_head_wrapper} ref={scrollHeadRef}>
              <div className={styles.thead_row}>{scrollColumns.map(renderHeaderCell)}</div>
            </div>
            <div className={styles.scroll_body} ref={scrollBodyRef}>
              {data.map((row, rowIdx) => (
                <div
                  key={rowIdx}
                  className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${selectedIds.has(rowIdx) ? styles.tr_selected : ""} ${
                    row.strike_through ? styles.tr_strike : ""
                  }`}
                  onClick={() => onRowClick?.(row)}
                  data-testid={`selectedRow${rowIdx}`}
                >
                  {scrollColumns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.table_scroll}>
        <div className={styles.thead_row}>
          {rowSelect && (
            <div className={`${styles.th} ${styles.th_checkbox}`}>
              <input
                type="checkbox"
                className={styles.checkbox}
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={handleSelectAll}
                aria-label="Select all rows"
              />
            </div>
          )}
          {columns.map(renderHeaderCell)}
        </div>
        {data.map((row, rowIdx) => (
          <div
            key={rowIdx}
            className={`${styles.tr} ${onRowClick ? styles.tr_clickable : ""} ${selectedIds.has(rowIdx) ? styles.tr_selected : ""} ${
              row.strike_through ? styles.tr_strike : ""
            }`}
            onClick={() => onRowClick?.(row)}
            data-testid={`selectedRow${rowIdx}`}
          >
            {rowSelect && (
              <div className={`${styles.td} ${styles.td_checkbox}`}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={selectedIds.has(rowIdx)}
                  disabled={row.isDisabled}
                  onChange={(e) => handleSelectRow(row, rowIdx, e)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={`Select row ${rowIdx + 1}`}
                />
              </div>
            )}
            {columns.map((col, colIdx) => renderBodyCell(row, col, colIdx))}
          </div>
        ))}
      </div>
    </div>
  );
};

DataTableV2.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string.isRequired,
      accessor: PropTypes.string.isRequired,
      fixed: PropTypes.bool,
      sortable: PropTypes.bool,
      width: PropTypes.string,
      minWidth: PropTypes.string,
      Cell: PropTypes.func,
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  onRowClick: PropTypes.func,
  onSort: PropTypes.func,
  rowSelect: PropTypes.bool,
  onRowSelect: PropTypes.func,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool,
};

export default DataTableV2;
